/**
 * Instrumentación temporal de tráfico JSON-RPC (ethers + viem/wagmi).
 * Activación: `VITE_DEBUG_RPC_TRAFFIC=true` o `localStorage DEBUG_RPC_TRAFFIC=1`.
 */
import type { JsonRpcProvider } from '@ethersproject/providers'

import { RPC_TRAFFIC_LOG_INTERVAL_MS } from '../constants/rpcPolling'
import { localStorageDebugOn, viteEnvDebugTrue } from './debugActivation'

const RPC_TRAFFIC_PREFIX = '[RPC_TRAFFIC]'

export function isRpcTrafficDebugEnabled(): boolean {
  if (typeof window !== 'undefined') {
    if (localStorageDebugOn(window.localStorage.getItem('DEBUG_RPC_TRAFFIC'))) return true
  }
  return viteEnvDebugTrue(import.meta.env.VITE_DEBUG_RPC_TRAFFIC)
}

const methodCounts: Record<string, number> = {}
let logTimer: ReturnType<typeof setInterval> | null = null

function ensureLogTimer(): void {
  if (typeof window === 'undefined' || logTimer != null) return
  logTimer = setInterval(() => {
    const parts = Object.entries(methodCounts)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([method, count]) => `${method}: ${count}`)
    if (parts.length) {
      console.info(`${RPC_TRAFFIC_PREFIX} (últimos ${RPC_TRAFFIC_LOG_INTERVAL_MS / 1000}s) ${parts.join(', ')}`)
    }
    for (const key of Object.keys(methodCounts)) {
      delete methodCounts[key]
    }
  }, RPC_TRAFFIC_LOG_INTERVAL_MS)
}

function parseRpcMethodFromBody(body: unknown): string | undefined {
  if (typeof body !== 'string') return undefined
  try {
    const parsed = JSON.parse(body) as { method?: unknown }
    return typeof parsed.method === 'string' ? parsed.method : undefined
  } catch {
    return undefined
  }
}

export function recordRpcTraffic(entry: { method: string; stack: string; chainId?: number }): void {
  if (!isRpcTrafficDebugEnabled()) return
  methodCounts[entry.method] = (methodCounts[entry.method] ?? 0) + 1
  ensureLogTimer()
  if (import.meta.env.DEV) {
    console.debug(`${RPC_TRAFFIC_PREFIX}`, entry)
  }
}

export function traceRpcFetchInit(init: RequestInit, stack: string, chainId?: number): void {
  const method = parseRpcMethodFromBody(init.body)
  if (method) {
    recordRpcTraffic({ method, stack, chainId })
  }
}

export function instrumentEthersProvider(provider: JsonRpcProvider, chainId: number): void {
  const originalSend = provider.send.bind(provider)
  provider.send = async (method: string, params: Array<unknown>) => {
    recordRpcTraffic({ method, stack: 'ethers', chainId })
    return originalSend(method, params)
  }
}
